import { getBaseUrl } from './commonUtils';
import { get } from './API';
import {
  MODAL_EXCEEDS_LIMITS,
  MODAL_GENERAL_UPGRADE,
  PERMISSION_READ,
  PERMISSION_WRITE,
  URL_DATASETS,
  URL_SPREADSHEET,
} from './constants';
import { convertToBytes } from './convertToBytes';
import getFileSize from './getFileSize';

import { getFileUuidFromPath, getFolderHandleFromPath } from './getFileUuidFromPath';
import { parseFileSize } from './parseFileSize';
import { showModal } from '@/redux/reducers/modalsSlice';

export function bytesToReadable(size) {
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i];
}

/**
 * Returns an array with arrays of the given size.
 *
 * @param myArray {Array} array to split
 * @param chunkSize {Integer} Size of every group
 */
export function chunkArray(myArray, chunkSize) {
  let index = 0;
  const arrayLength = myArray.length;
  const tempArray = [];

  for (index = 0; index < arrayLength; index += chunkSize) {
    const chunk = myArray.slice(index, index + chunkSize);
    // Do something if you want with the group
    tempArray.push(chunk);
  }

  return tempArray;
}

export const openInNewTab = (link) => window.open(link, '_blank', 'noopener', 'noreferrer');

export const openSpreadsheet = (uuid, spreadsheetName) => {
  localStorage.setItem('fileName', spreadsheetName);
  localStorage.setItem('uuid', uuid);
  const linkTo = `/${URL_SPREADSHEET}/${formatUrlSafeFileName(spreadsheetName)}/${uuid}`;
  openInNewTab(linkTo);
};

export const getFilesSizeInPercentage = (size, maxStorageSize) => {
  let currentSizeInBytes = size;
  if (isNaN(size)) {
    currentSizeInBytes = convertToBytes(size) || 0;
  }
  const maxSizeInBytes = convertToBytes(maxStorageSize);

  return +Number((currentSizeInBytes / maxSizeInBytes) * 100).toFixed(2);
};

export const getAllFilesSize = (data) => {
  const allSize = data?.reduce((total, item) => total + getIndividualFileSize(item.FileSize), 0);

  if (!allSize || isNaN(allSize)) {
    return 0;
  }

  return parseFileSize(allSize);
};

export const getIndividualFileSize = (fileSize) => {
  if (isNaN(fileSize) && fileSize !== '') {
    return Number(fileSize.split(' ')[0]);
  } else if (fileSize === '') {
    return 0;
  } else {
    return Number(fileSize);
  }
};

export const getUniqueArrayOfObjectsByProperty = (arr, property) => [
  ...new Map(arr.map((item) => [item[property], item])).values(),
];

export const getSelectionRange = (start, end) => {
  if (start < end) return [start, end + 1];
  if (start > end) return [end, start + 1];
  return [start, end];
};

const hasReadOrWriteAccess = (permissions) => {
  return permissions?.includes(PERMISSION_READ) || permissions?.includes(PERMISSION_WRITE);
};

export const getReadOrWriteAccessForAllPermissionTypes = async ({
  fileUUID,
  isAnonymous,
  filePath,
  user,
}) => {
  try {
    let filePermissions;
    let orgPermissions;
    let userFilePermissions;

    if (isAnonymous) {
      const response = await get(`dataset/${fileUUID}/file-permissions`, true);
      if (!response.ok) return [false, false, false];
      filePermissions = (await response.json())?.permissions;
    } else {
      const filePathEntry = filePath.find(({ metadata }) => metadata?.FileUuid === fileUUID);
      if (!filePathEntry) return [false, false, false];

      const { public: publicPermissions, organization, restricted } = filePathEntry.shared || {};
      filePermissions = publicPermissions;
      orgPermissions = organization;
      userFilePermissions = restricted?.find(({ user_id }) => user_id === user?.email)?.permissions;
    }

    const checkAccess = (permissions) => (permissions ? hasReadOrWriteAccess(permissions) : false);

    return [
      checkAccess(filePermissions),
      checkAccess(orgPermissions),
      userFilePermissions?.length ? hasReadOrWriteAccess(userFilePermissions) : false,
    ];
  } catch (error) {
    console.error('Error fetching permissions:', error);
    return [false, false, false];
  }
};

export const getIsResourcePublic = (user, isAnonymous = false, filePath = []) => {
  let fileUUID = '';
  if (location.pathname.includes(`/${URL_DATASETS}`)) {
    fileUUID = getFolderHandleFromPath();
  } else {
    fileUUID = getFileUuidFromPath();
  }
  if (fileUUID?.length > 0) {
    return getReadOrWriteAccessForAllPermissionTypes({
      fileUUID,
      isAnonymous,
      filePath,
      user,
    }).then((values) => {
      const [hasFileReadOrWriteAccess, hasOrgReadOrWriteAccess, hasUserFileReadOrWriteAccess] =
        values;
      return hasFileReadOrWriteAccess && !hasOrgReadOrWriteAccess && !hasUserFileReadOrWriteAccess;
    });
  } else {
    return Promise.resolve(false);
  }
};

export const checkFileExceedsQuota = (filesSize, file, user) => {
  const maxStorageSize = user?.userProperties.maxStorageSize;
  return (
    convertToBytes(filesSize) + getFileSize(file?.metadata?.FileSize || '') >
    convertToBytes(maxStorageSize)
  );
};

export const formatUrlSafeFileName = (fileName = '') => {
  return fileName.replace(/\W/g, '-');
};

export const getNumberValueSeparatedByCommas = (value) => {
  if (!value) return 0;
  if (isNaN(Number(value))) return value;
  return Number(value).toLocaleString('en-US');
};

export const postToLogoutChannel = () => {
  const broadcastChannel = new BroadcastChannel('logoutChannel');
  broadcastChannel.postMessage({ type: 'logout' });
};

export const getWebsocketHostname = () => {
  const baseHost = process.env.REACT_APP_API_SERVICE_BASE_HOST;
  return baseHost.replace('http', 'ws');
};

const generateAuthStateNonce = () => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0].toString(16);
};
export const getAuthNonceAndSetInStorage = () => {
  const authStateNonce = generateAuthStateNonce();
  localStorage.setItem('authStateNonce', authStateNonce);
  return authStateNonce;
};

export const camelCaseToNormal = (camelCase) => {
  return camelCase
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};
export const throwNewErrorWithMessage = (message) => {
  const errorWithMessage = new Error();
  errorWithMessage.message = message || '';
  throw errorWithMessage;
};

export const isCypressRun = () => {
  return (
    localStorage.getItem('cypressGigasheetAPIToken') !== null &&
    getBaseUrl('webapp') === 'http://localhost:3000'
  );
};

export const checkIsMRFSheet = (parserUsed = '') => {
  return /^healthcare .* parser$/.test(parserUsed);
};

export const checkIsJSONSheet = (parserUsed = '') => {
  return /^.*json.* parser$/i.test(parserUsed);
};

export const checkCanAnalyzeJson = (userProperties = {}) => {
  return userProperties.canAnalyzeJson === true;
};

export const triggerPreviewModeUpsellModal = ({ dispatch, currentFile, user, title }) => {
  if (currentFile?.metadata?.Owner === user?.email || currentFile?.OverRowQuota) {
    const isOverQuota = !currentFile?.WithinQuota;
    const isFileRowOverQuota = currentFile?.OverRowQuota;
    const isMRFSheet = checkIsMRFSheet(currentFile?.metadata?.ParserUsed);
    const isJSONSheet = checkIsJSONSheet(currentFile?.metadata?.ParserUsed);
    const canAnalyzeJson = checkCanAnalyzeJson(user?.userProperties);

    let premiumFeature = 'general';
    if (isOverQuota) {
      premiumFeature = 'fileSize';
    } else if (isFileRowOverQuota) {
      premiumFeature = 'fileExceedsRowLimit';
    } else if ((isMRFSheet || isJSONSheet) && !canAnalyzeJson) {
      premiumFeature = 'jsonAnalyst';
    }
    if (title) {
      premiumFeature = 'base';
    }

    dispatch(
      showModal({
        name: MODAL_GENERAL_UPGRADE,
        props: { premiumFeature, title },
      })
    );
  } else {
    dispatch(showModal({ name: MODAL_EXCEEDS_LIMITS }));
  }
};
